import React from 'react';
import { motion } from 'framer-motion';
import { ThumbsUp, ThumbsDown, AlertTriangle, Lightbulb, Sparkles, Building2 } from 'lucide-react';
import { Card } from "@/components/ui/card";
import { Link } from 'react-router-dom';
import { createPageUrl } from '../../utils';
import { safeArray } from '@/components/utils/safeArray';

export default function ProductAnalysis({ product }) {
  const Section = ({ icon: Icon, title, items, color = "gray" }) => {
    if (!items || items.length === 0) return null;

    const colors = {
      green: { bg: 'bg-emerald-50', text: 'text-emerald-700', icon: 'text-emerald-600' },
      red: { bg: 'bg-red-50', text: 'text-red-700', icon: 'text-red-600' },
      yellow: { bg: 'bg-amber-50', text: 'text-amber-700', icon: 'text-amber-600' },
      blue: { bg: 'bg-blue-50', text: 'text-blue-700', icon: 'text-blue-600' },
    };

    const currentColor = colors[color] || colors.gray;

    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-4"
      >
        <div className="flex items-center gap-2 mb-3">
          <div className={`w-8 h-8 rounded-lg ${currentColor.bg} flex items-center justify-center`}>
            <Icon className={`w-4 h-4 ${currentColor.icon}`} />
          </div>
          <h3 className="font-semibold text-gray-900">{title}</h3>
        </div>
        
        <ul className="space-y-2 ml-10">
          {items.map((item, index) => (
            <li key={index} className="flex items-start gap-2">
              <span className={`w-1.5 h-1.5 rounded-full ${currentColor.bg} mt-2 flex-shrink-0`} />
              <span className="text-sm text-gray-700">{item}</span>
            </li>
          ))}
        </ul>
      </motion.div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Recommendation */}
      {product.recommendation && (
        <Card className="p-4 bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200">
          <div className="flex gap-3">
            <Sparkles className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-emerald-900 mb-1">Recomendación personalizada</p>
              <p className="text-sm text-emerald-800">{product.recommendation}</p>
            </div>
          </div>
        </Card>
      )}

      {/* Warnings */}
      {safeArray(product.warnings).length > 0 && (
        <Card className="p-4 bg-amber-50 border-amber-200">
          <div className="flex gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-amber-900 mb-2">⚠️ Importante para vos</p>
              <ul className="space-y-1">
                {safeArray(product.warnings).map((warning, index) => (
                  <li key={index} className="text-sm text-amber-800">• {warning}</li>
                ))}
              </ul>
            </div>
          </div>
        </Card>
      )}

      {/* Pros & Cons */}
      <div className="grid sm:grid-cols-2 gap-4">
        {safeArray(product.pros).length > 0 && (
          <Card className="p-4">
            <Section icon={ThumbsUp} title="Lo bueno" items={product.pros} color="green" />
          </Card>
        )}
        
        {safeArray(product.cons).length > 0 && (
          <Card className="p-4">
            <Section icon={ThumbsDown} title="Lo malo" items={product.cons} color="red" />
          </Card>
        )}
      </div>

      {/* Additives */}
      {safeArray(product.additives).length > 0 && (
        <Card className="p-4">
          <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600" />
            Aditivos detectados
          </h3>
          <div className="space-y-3">
            {safeArray(product.additives).map((additive, index) => (
              <div key={index} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${
                  additive.risk_level === 'alto' ? 'bg-red-500' :
                  additive.risk_level === 'moderado' ? 'bg-amber-500' :
                  'bg-green-500'
                }`} />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm text-gray-900">
                    {additive.name} {additive.code && `(${additive.code})`}
                  </p>
                  <p className="text-xs text-gray-600 mt-1 line-clamp-2">{additive.description}</p>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Alternatives */}
      {safeArray(product.alternatives).length > 0 && (
        <Card className="p-4 bg-blue-50 border-blue-200">
          <div className="flex gap-3">
            <Lightbulb className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-blue-900 mb-2">💡 Alternativas mejores</p>
              <ul className="space-y-1">
                {safeArray(product.alternatives).map((alt, index) => (
                  <li key={index} className="text-sm text-blue-800">• {alt}</li>
                ))}
              </ul>
            </div>
          </div>
        </Card>
      )}

      {/* Manufacturer Link */}
      {product.brand && (
        <Link to={createPageUrl('Manufacturer') + `?brand=${encodeURIComponent(product.brand)}`}>
          <Card className="p-4 hover:bg-gray-50 transition-colors cursor-pointer">
            <div className="flex items-center gap-3">
              <Building2 className="w-5 h-5 text-blue-600" />
              <div>
                <p className="text-xs text-gray-500">Fabricante</p>
                <p className="font-semibold text-gray-900">{product.brand}</p>
              </div>
            </div>
          </Card>
        </Link>
      )}

      {/* Nutrition Info */}
      {product.nutrition_data && Object.keys(product.nutrition_data).length > 0 && (
        <Card className="p-4">
          <h3 className="font-semibold text-gray-900 mb-3">Información nutricional</h3>
          <div className="grid grid-cols-2 gap-3">
            {product.nutrition_data.calories && (
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-gray-900">{product.nutrition_data.calories}</p>
                <p className="text-xs text-gray-500">Calorías</p>
              </div>
            )}
            {product.nutrition_data.sugar !== undefined && (
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-gray-900">{product.nutrition_data.sugar}g</p>
                <p className="text-xs text-gray-500">Azúcar</p>
              </div>
            )}
            {product.nutrition_data.sodium !== undefined && (
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-gray-900">{product.nutrition_data.sodium}mg</p>
                <p className="text-xs text-gray-500">Sodio</p>
              </div>
            )}
            {product.nutrition_data.fats !== undefined && (
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-gray-900">{product.nutrition_data.fats}g</p>
                <p className="text-xs text-gray-500">Grasas</p>
              </div>
            )}
          </div>
          {product.nutrition_data.serving_size && (
            <p className="text-xs text-gray-400 text-center mt-3">
              Por {product.nutrition_data.serving_size}
            </p>
          )}
        </Card>
      )}
    </div>
  );
}